import { Backpack, Eye, Navigation, Wallet } from "lucide-react";
import { FeatureCard } from "@/components/site/Stickers";
import { ENTRY_FEES, formatCOP } from "@/constants";
import { getParkStatus } from "@/lib/closures";
import cabo from "@/assets/campingcabo.avif";
import bahia from "@/assets/tayrona-extra3.avif";
import mirador from "@/assets/tayrona-extra2.avif";
import costa from "@/assets/tayrona-extra4.avif";
import panoramica from "@/assets/tayrona-extra1.avif";

/**
 * "Planea tu visita." Mindtrip-style feature row for the home page. Each
 * card links to the section below that holds the detail, so on this long
 * page it doubles as a table of contents.
 *
 * Stickers carry real facts only (DESIGN.md). Status and fees are read live;
 * the rest quote the FAQ / closures copy on this page, keep them in sync:
 *   - "3 al año, sin excepciones": ParkClosures.tsx
 *   - "$3.000 por persona", bus Santa Marta "$9.000, 1 hora", Cartagena "5 horas": FAQ.tsx
 *   - "No hay cajeros dentro", "Mínimo 2 L por día": PackingList.tsx
 */
const PlanVisit = () => {
  const { status, label } = getParkStatus();
  const [joven, adulto, extranjero] = ENTRY_FEES;

  const cards = [
    {
      icon: Eye,
      title: "Revisa.",
      text: "Mira si el parque está abierto y cuándo cierra antes de reservar nada.",
      link: { href: "#cierres", label: "Ver fechas de cierre" },
      stickers: [
        { img: panoramica, label: "Parque Tayrona hoy", fact: label, tone: status === "open" ? ("good" as const) : ("alert" as const), tilt: -2 },
        { img: costa, label: "Cierres", fact: "3 al año, sin excepciones", tilt: 3 },
      ],
    },
    {
      icon: Wallet,
      title: "Calcula.",
      text: "La entrada cambia según tu edad, de dónde eres y la temporada.",
      link: { href: "#tourism-info", label: "Ver todas las tarifas" },
      stickers: [
        { img: mirador, label: "Colombiano, mayor de 25 años", fact: `${formatCOP(adulto.lowSeasonCOP)} a ${formatCOP(adulto.highSeasonCOP)}`, tilt: 2 },
        { img: bahia, label: "De 5 a 25 años", fact: `Desde ${formatCOP(joven.lowSeasonCOP)}`, tone: "good" as const, tilt: -3 },
        { img: costa, label: "Extranjero no residente", fact: `Desde ${formatCOP(extranjero.lowSeasonCOP)}`, tilt: 1 },
      ],
    },
    {
      icon: Navigation,
      title: "Llega.",
      text: "Cómo llegar a la entrada de El Zaino desde Santa Marta, Cartagena o Bogotá.",
      link: { href: "#faq", label: "Ver cómo llegar" },
      stickers: [
        { img: panoramica, label: "Santa Marta a El Zaino", fact: "Bus $9.000, 1 hora", tilt: -2 },
        { img: mirador, label: "Cartagena a El Zaino", fact: "Bus, unas 5 horas", tilt: 3 },
      ],
    },
    {
      icon: Backpack,
      title: "Empaca.",
      text: "Lo esencial para la caminata y la noche en carpa o hamaca.",
      link: { href: "#lista-equipaje", label: "Ver la lista completa" },
      stickers: [
        { img: cabo, label: "Dentro del parque", fact: "No hay cajeros", tone: "alert" as const, tilt: 2 },
        { img: bahia, label: "Agua potable", fact: "Mínimo 2 L por día", tilt: -3 },
      ],
    },
  ];

  return (
    <section aria-labelledby="plan-h" className="pq-section pq-mist">
      <div className="pq-wrap" style={{ display: "grid", gap: 48 }}>
        <div style={{ display: "grid", gap: 16 }}>
          <h2 id="plan-h" className="pq-headline">Planea tu visita.</h2>
          <p className="pq-lead">
            Lo que revisa un samario antes de entrar al Tayrona. Toca una tarjeta
            para ver el detalle.
          </p>
        </div>
        <div className="pq-features">
          {cards.map((c) => (
            <FeatureCard key={c.title} icon={c.icon} title={c.title} link={c.link} stickers={c.stickers}>
              {c.text}
            </FeatureCard>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PlanVisit;
